import { useSalesEntry } from "contexts/SalesEntryContext";
import DataGrid, {
  Column,
  Summary,
  TotalItem,
} from "devextreme-react/cjs/data-grid";
import notistack from "modules/core/notistack";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";

interface Payment {
  id: string;
  type: string;
  amount: number;
  referenceNumber: string;
  discountAmount: number;
}

interface PaymentData {
  method: string;
  amount: number;
  referenceNumber: string;
  discountAmount: number;
}

interface PaymentModalProps {
  show: boolean;
  onHide: () => void;
  currentPayment: PaymentData[] | null;
  onPaymentSubmit: (payment: PaymentData[] | null) => void;
  onCouponRemove?: (couponCode: string, couponData: any) => void; // ✅ New prop for coupon removal
}

interface FormErrors {
  paymentType?: string;
  amount?: string;
  referenceNumber?: string;
  customerMobile?: string;
}

interface PaymentFormData {
  paymentType: string;
  amount: string;
  referenceNumber: string;
  customerMobile: string;
}

interface HKDiscountItem {
  id: string;
  selected: boolean;
  material: string;
  priceType: string;
  discountOn: string;
  discountType: string;
  perAmt: string;
  discount: string;
  invoiceRangeFrom: number;
  invoiceRangeTo: number;
  productCode?: string;
  productName?: string;
  weight?: number;
  price?: number;
  karat?: string;
  quantity?: number;
  making?: number;
}

const paymentTypes = ["Cash", "Card", "UPI", "Net Banking", "Cheque", "Credit"];

export const PaymentModal: React.FC<PaymentModalProps> = ({
  show,
  onHide,
  currentPayment,
  onPaymentSubmit,
  onCouponRemove,
}) => {
  const { state, actions } = useSalesEntry();
  // console.log(state?.customer?.creditAmount)
  const [formData, setFormData] = useState<PaymentFormData>({
    paymentType: "",
    amount: "",
    referenceNumber: "",
    customerMobile: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [payments, setPayments] = useState<Payment[]>([]);

  // Populate form with existing data when modal opens
  useEffect(() => {
    if (show && currentPayment && currentPayment.length > 0) {
      // Convert all current payments to modal payment format
      const existingPayments: Payment[] = currentPayment.map(
        (payment, index) => ({
          id: `existing_payment_${index}`,
          type: payment.method,
          amount: payment.amount,
          referenceNumber: payment.referenceNumber || "-",
          discountAmount: payment.discountAmount || 0,
        })
      );
      setPayments(existingPayments);
      setFormData({
        paymentType: "",
        amount: "",
        referenceNumber: "",
        customerMobile: "",
      });
    } else if (show && (!currentPayment || currentPayment.length === 0)) {
      setPayments([]);
      setFormData({
        paymentType: "",
        amount: "",
        referenceNumber: "",
        customerMobile: "",
      });
    }
    setErrors({});
  }, [show, currentPayment]);

  const validatePaymentForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData?.paymentType.trim()) {
      newErrors.paymentType = "Payment type is required";
    }

    const amount = Number(formData.amount);
    const creditLimit = Number(state?.customer?.creditAmount ?? 0);

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required";
    } else if (isNaN(amount) || amount <= 0) {
      newErrors.amount = "Amount must be a positive number";
    }
    else if (formData.paymentType === "Credit" && amount > creditLimit) {
      newErrors.amount = `Amount cannot exceed credit limit of ₹${creditLimit}`
    }
    else if (formData.paymentType === "Credit" &&
      typeof state?.customer?.creditAmount === "number" &&
      amount > state.customer.creditAmount &&
      amount > (state.summary?.totalDueAmt ?? 0)
    ) {

      newErrors.amount = `Amount cannot exceed customer's credit amount of ₹${state.customer.creditAmount}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const validateVoucherForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.customerMobile.trim()) {
      newErrors.customerMobile = "Customer mobile is required";
    } else if (!/^\d{10}$/.test(formData.customerMobile)) {
      newErrors.customerMobile = "Please enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddPayment = () => {
    if (!validatePaymentForm()) {
      return;
    }
    const amount = Number(formData.amount);
    const creditAmount = state?.customer?.creditAmount ?? 0;
    const totalDue = state?.summary?.totalDueAmt ?? 0;
    if (
      formData.paymentType === "Credit" &&
      (amount <= creditAmount && amount > totalDue)
    ) {
      notistack.warning("Amount cannot exceed");
      return;
    }
    if (
      formData.paymentType === "Credit" &&
      (!state?.customer?.creditAmount || state.customer.creditAmount <= 0)
    ) {
      notistack.warning("This customer has no credit available. Credit payment cannot be added.");
      return;
    }
    // Prevent adding multiple Credit payments
    if (
      formData.paymentType === "Credit" &&
      payments.some((p) => p.type === "Credit")
    ) {
      notistack.warning("Credit payment can only be added once.");
      return;
    }

    const newPayment: Payment = {
      id: String(Date.now()),
      type: formData?.paymentType,
      amount: Number(formData?.amount),
      referenceNumber: formData?.referenceNumber || "-",
      discountAmount: 0,
    };

    const updatedPayments = [...payments, newPayment];
    setPayments(updatedPayments);

    // Reset payment form
    setFormData((prev) => ({
      ...prev,
      paymentType: "",
      amount: "",
      referenceNumber: "",
    }));
    setErrors({});

    actions.clearPayments();
    updatedPayments.forEach(payment => {
      actions.addPayment({
        method: payment.type,
        amount: payment.amount,
        referenceNumber: payment.referenceNumber || "",
        discountAmount: payment.discountAmount || 0,
      });
    });


  };

  useEffect(() => {
    if (!state?.customer) {
      const filteredPayments = payments.filter((p) => p.type !== "Credit");
      if (filteredPayments.length !== payments.length) {
        setPayments(filteredPayments);

        actions.clearPayments();
        filteredPayments.forEach((payment) => {
          actions.addPayment({
            method: payment.type,
            amount: payment.amount,
            referenceNumber: payment.referenceNumber || "",
            discountAmount: payment.discountAmount || 0,
          });
        });

        notistack.info("Credit payment removed because customer was cleared.");
      }
    }
  }, [state?.customer, show, payments]);


  const handleRemovePayment = (paymentId: string) => {
    const paymentToRemove = payments.find(p => p.id === paymentId);

    // Check if this is a Gift Voucher payment linked to a coupon
    if (
      paymentToRemove?.type === 'Gift Voucher' &&
      paymentToRemove?.referenceNumber &&
      paymentToRemove?.referenceNumber !== '-' &&
      state.coupon &&
      state.coupon.code === paymentToRemove.referenceNumber
    ) {
      // Remove the coupon directly without asking confirmation
      if (onCouponRemove) {
        onCouponRemove(paymentToRemove.referenceNumber, null);
      }
      toast.info(`Coupon "${paymentToRemove.referenceNumber}" removed because its Gift Voucher payment was deleted.`);
    }

    // Remove from local state
    const updatedPayments = payments.filter(p => p.id !== paymentId);
    setPayments(updatedPayments);

    // Sync with context by clearing and rebuilding
    actions.clearPayments();
    updatedPayments.forEach(payment => {
      actions.addPayment({
        method: payment.type,
        amount: payment.amount,
        referenceNumber: payment.referenceNumber || "",
        discountAmount: payment.discountAmount || 0,
      });
    });

    // Show success message
    if (paymentToRemove) {
      toast.success(`${paymentToRemove.type} payment of ₹${paymentToRemove.amount} removed successfully.`);
    }
  };


  const handleSubmit = () => {
    // Submit all payments as array
    if (payments.length > 0) {
      const paymentData: PaymentData[] = payments.map((payment) => ({
        method: payment.type,
        amount: payment.amount,
        referenceNumber: payment.referenceNumber || "",
        discountAmount: payment.discountAmount || 0,
      }));
      onPaymentSubmit(paymentData);
    } else {
      onPaymentSubmit(null);
    }
    handleClose();
  };

  const handleRemove = () => {
    // Check if there are any Gift Voucher payments linked to coupons
    const giftVoucherPayments = payments.filter(p =>
      p.type === 'Gift Voucher' &&
      p.referenceNumber &&
      p.referenceNumber !== '-'
    );

    if (giftVoucherPayments.length > 0 && state.coupon) {
      const linkedVoucher = giftVoucherPayments.find(
        p => p.referenceNumber === state.coupon?.code
      );

      // If a linked coupon exists, remove it
      if (linkedVoucher && onCouponRemove) {
        onCouponRemove(linkedVoucher.referenceNumber, null);
        toast.info(
          `Coupon "${linkedVoucher.referenceNumber}" removed because all payments were cleared.`
        );
      }
    }

    // Clear all payments from context and local state
    actions.clearPayments();
    setPayments([]);
    onPaymentSubmit(null);
    handleClose();
  };


  const handleClose = () => {
    setFormData({
      paymentType: "",
      amount: "",
      referenceNumber: "",
      customerMobile: "",
    });
    setPayments([]);
    setErrors({});
    onHide();
  };

  const updateField = (field: keyof PaymentFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const getTotalAmount = () => {
    const totalAmount = payments.reduce((total, payment) => total + payment.amount, 0);
    return (totalAmount.toFixed(2));
    // return payments.reduce((total, payment) => total + payment.amount, 0);
  };

  const getTotalDiscountAmount = () => {
    return payments.reduce(
      (total, payment) => total + payment.discountAmount,
      0
    );
  };

  const isGiftVoucherPayment = (payment: Payment) => {
    return payment.type === 'Gift Voucher' &&
      payment.referenceNumber &&
      payment.referenceNumber !== '-' &&
      state.coupon?.code === payment.referenceNumber;
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">
          <i className="bx bx-credit-card me-2"></i>
          Add Payment
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {payments.length > 0 && (
          <div className="alert alert-info mb-3">
            <i className="bx bx-info-circle me-2"></i>
            Current payments: <strong>{payments.length} payment(s)</strong> -
            Total: <strong>₹{getTotalAmount().toLocaleString()}</strong>
            {payments.some(p => isGiftVoucherPayment(p)) && (
              <div className="mt-2">
                <small className="text-warning">
                  <i className="bx bx-warning me-1"></i>
                  Gift Voucher payments are linked to coupons. Removing them will also remove the associated coupon.
                </small>
              </div>
            )}
          </div>
        )}

        {/* Add Payment Form */}
        <Form>
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Payment Type <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={formData.paymentType}
                  onChange={(e) => updateField("paymentType", e.target.value)}
                  isInvalid={!!errors.paymentType}
                >
                  <option value="">Please Select</option>
                  {paymentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.paymentType}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;

                    const regex = /^(\d{0,9}(\.\d{0,2})?)$/;

                    if (regex.test(value)) {
                      updateField("amount", value);
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()} // Optional, can remove if it disrupts UX
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", ",", " "].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  isInvalid={!!errors.amount}
                  min="0"
                  step="0.01"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.amount || (formData.amount.length > 10 ? "Amount cannot exceed 9 digits" : "")}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="text-secondary">
                  Reference No.
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter reference number"
                  value={formData.referenceNumber}
                  onChange={(e) =>
                    updateField("referenceNumber", e.target.value)
                  }
                  maxLength={25}
                  onInput={(e: any) => {
                    let value = e.target.value.replace(/\s+/g, ' ');
                    if (value.startsWith(' ')) {
                      value = value.trimStart();
                    }
                    e.target.value = value;
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleAddPayment}
                className={`me-2 ${errors.amount ? "mb-4" : ''}`}
                style={{ height: "38px" }}
              >
                <i className="bx bx-plus"> Add Payment</i>
              </Button>
            </Col>
          </Row>
        </Form>

        {payments.length > 0 && (
          <div className="mb-4 w-100">
            <Table striped hover responsive>
              <thead style={{ backgroundColor: '#b7002a' }}>
                <tr>
                  <th style={{ width: '100px', color: 'white' }}>Actions</th>
                  <th style={{ color: 'white' }}>Type</th>
                  <th style={{ color: 'white' }}>Reference Number</th>
                  <th style={{ color: 'white' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment.id}>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemovePayment(payment.id)}
                        title={
                          isGiftVoucherPayment(payment)
                            ? "Delete payment and remove linked coupon"
                            : "Delete payment"
                        }
                      >
                        <i className="bx bx-trash" />
                      </Button>
                    </td>
                    <td>{payment.type}</td>
                    <td>{payment.referenceNumber}</td>
                    <td>{payment.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted">
            <small>
              <strong>Total Payments:</strong> {payments.length} |
              <strong> Amount:</strong> ₹{getTotalAmount().toLocaleString()}
            </small>
          </div>
          <div>
            {payments.length > 0 && (
              <Button variant="warning" onClick={handleRemove} className="ms-2 me-2">
                {/* <i className="bx bx-trash me-1"></i> */}
                Clear All
              </Button>
            )}
            <Button className="add-btn" onClick={handleClose}>
              Cancel
            </Button>

            <Button onClick={handleSubmit} className="ms-2 submit-btn">
              {/* <i className="bx bx-check me-1"></i> */}
              {currentPayment && currentPayment.length > 0
                ? "Update"
                : "Save"}{" "}
              Payment
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};